import { isGuid } from './identify';

/**
 * The loaders behind `LookupSelect`, in one place.
 *
 * Each is `(api, key) => Promise<[{ label, value }]>` and each has to answer two
 * different questions with the same call: "what matches what I typed" while
 * somebody is typing, and "what is this id called" when a value arrives from the
 * URL. Every loader below handles an id as well as a name, which is what makes a
 * pasted link show a facility's name instead of its GUID.
 *
 * Facility, provider and product go through the api modules' own
 * `lookupByNameOrId`, which already does both.
 *
 * **Users do not.** `users.lookupByEmailOrId` runs the key through
 * `determineFieldType`, which only accepts an id or a *complete, valid* email —
 * type `mette` and it returns nothing at all, silently. The users search endpoint
 * matches id, name, email and phone, so that is what this uses instead: same
 * data, and the field behaves like the others.
 */

const first = value => (Array.isArray(value) ? value : []);

export const lookupUser = (api, key) =>
  api.users.searchUsers({ key, skipPages: 1, resultsCount: 8 })
    .then(rows => first(rows).map(row => ({
      value: row.Id || row.id,
      label: [row.Name || row.name, row.Email || row.email].filter(Boolean).join(' — ')
        || (row.Id || row.id)
    })));

export const lookupFacility = (api, key) => api.facilities.lookupByNameOrId(key);

// The historical sales report searches a different source: `lookupByName` goes to
// `/admin/facility/search-from-report-table`, which is the table that report reads
// from. A facility missing from it cannot appear in that report, so offering the
// live facility list there would offer choices that return nothing. Name only —
// that endpoint has no id filter.
//
// **It does work**, despite reading as though it cannot. The parameter is called
// `FacilityName` and the form fills it from the option's `value`, which is the
// shape of an id everywhere else in this admin — so it looks like an id being
// compared to a name, and the earlier pass flagged it as a filter that silently
// matches nothing. It is not: the endpoint projects `Id = x.ProviderName`, so the
// "id" it hands out *is* the name, and `SalesReportOldShared` compares
// `x.ProviderName == facilityName` against exactly that. Confirmed against both
// sides; nothing to fix, and worth leaving written down so nobody spends the
// afternoon on it again.
export const lookupReportFacility = (api, key) => api.facilities.lookupByName(key);

export const lookupProvider = (api, key) => api.providers.lookupByNameOrId(key);

/**
 * Type 1 is a product, 2 a membership; without a type the search covers both.
 *
 * **The id branch of `products.lookupByNameOrId` throws.** It calls
 * `getMembership(key, …)`, and no function of that name exists in
 * `_products.js` — it is `getMembershipProduct`. So pasting a product id into
 * any of these filters raises a `ReferenceError` and the field goes quietly
 * empty, in the old admin as well as here (`Subscriptions.js` and
 * `SeasonalCourts.js` call the same function).
 *
 * Not fixed in place, for the reason the provider search is not: it is a shared
 * module, and repairing it changes what the old admin does. It also needs a
 * decision that is not mine — the id branch fetches a *membership* product for
 * what may be an ordinary one, so "fix the name" and "fix the behaviour" are two
 * different changes.
 *
 * This tree handles the id itself instead, with `getProduct`, and falls back to
 * the shared search for a name. Noted in NIGHT-STATE.md.
 */
const productById = (api, id) => api.products.getProduct(id)
  .then(product => (product && (product.Id || product.id)
    ? [{ value: product.Id || product.id, label: product.Name || product.name || product.Id }]
    : []))
  .catch(() => []);

const productLookup = type => (api, key) => (isGuid(key)
  ? productById(api, key)
  : api.products.lookupByNameOrId(key, type).then(rows => rows || []));

export const lookupProduct = productLookup(undefined);
export const lookupMembership = productLookup(2);
export const lookupBookableProduct = productLookup(1);

/**
 * Two lookups that exist only for the command palette's paste case.
 *
 * Neither is a *search* — both take an id and answer at most one row — and that
 * is the point. A pasted id is most often a signup or a purchase, because those
 * are what support and operations deal in, and until these ran the palette could
 * only offer them as guesses in the "open this id as…" list. Now the id comes
 * back with a facility and a person's name on it, which is the difference
 * between "this might be a signup" and "this is Mette's Tuesday booking at
 * Hjortshøj".
 *
 * Called only when the query is a GUID: they cost a request each, and a name is
 * never an id.
 */
export const lookupSignupById = (api, id) =>
  api.signups.searchSignups({ filters: { id, resultsPerPage: 1 } })
    .then(data => (data?.signups || [])
      .filter(row => row && row.id)
      .map(row => ({
        value: row.id,
        label: [row.facilityName, row.userName, row.activityDateCet && String(row.activityDateCet).slice(0, 10)]
          .filter(Boolean).join(' · ') || row.id
      })));

export const lookupPurchaseById = (api, purchaseId) =>
  api.purchases.searchPurchase({ filters: { purchaseId }, resultsCount: 1 })
    .then(data => {
      const rows = Array.isArray(data) ? data : (data?.Purchases || []);
      return rows
        .filter(row => row && (row.Id || row.id))
        .map(row => ({
          value: row.Id || row.id,
          label: [row.UserName, row.Status, row.CreatedAtCet && String(row.CreatedAtCet).slice(0, 10)]
            .filter(Boolean).join(' · ') || (row.Id || row.id)
        }));
    });
